import type { GeneratedDocument } from '../../types/analysis.types';

export const vibeMapperDocuments: GeneratedDocument[] = [
  {
    fileName: 'ai-docs/EVIDENCE.md',
    title: 'Evidence Log',
    content: `## Build & Dependencies

- **Package Managers:** pnpm (TypeScript/React), pip (Python), poetry (AI engine)
- **Runtime Dependencies:** ~245 packages
- **Dev Dependencies:** ~125 packages
- **Build Tools:** Vite (client), TypeScript compiler (server), Docker multi-stage builds
- **IaC:** Terraform modules, Helm charts

## Testing

- **Test Frameworks:** Playwright (E2E), pytest (Python), Vitest (unit)
- **Test Files:** 42 test files across sub-projects
- **Coverage:** v8 coverage configured, threshold not enforced in CI
- **E2E:** Playwright browser automation for critical user flows

## CI/CD

- **CI Platform:** GitLab CI (.gitlab-ci.yml)
- **Stages:** build, test, deploy
- **Workflows:** 1 multi-project pipeline (LiveSite + App/Site)
- **Deployment:** Manual trigger for staging/production, Cloudflare Pages for LiveSite

## Security

- **Secrets Management:** HashiCorp Vault (AppRole + JWT/OIDC auth)
- **Vault Agent:** Renders /secrets/.env.runtime at container startup
- **Dependency Scanning:** Not configured
- **SAST/DAST:** Not detected
- **Secret Detection:** Not configured

## Documentation

- **README:** Present (comprehensive)
- **CLAUDE.md:** Detailed AI agent configuration (root + sub-projects)
- **Architecture Docs:** docs/deployment/, docs/security/
- **Changelog:** Present
- **License:** Present
- **Contributing:** Present
- **Doc Count:** 28 files in docs/

## Source Control

- **VCS:** Git (GitLab)
- **Branch Strategy:** Trunk-based (main + feature branches)
- **Commit Activity:** 796 commits in last 30 days, 9 active contributors
- **Merge Requests:** 7 open, 26 merged in last 30 days
`,
  },
  {
    fileName: 'ai-docs/FINDINGS.md',
    title: 'SDLC Findings',
    content: `## Overall Score: 74/100 (Grade B)

## Category Scores

| Category | Score | Status |
|----------|-------|--------|
| Source Control | 85 | Detected |
| Build & Integration | 85 | Detected |
| Testing & Quality | 100 | Detected |
| Deployment | 100 | Detected |
| Security | 60 | Partial |
| Collaboration | 20 | Partial |
| Documentation | 100 | Detected |
| AI Tooling | 40 | Partial |

## Source Control (Score: 85/100)
**Status:** Detected

**Strengths:**
- Active GitLab repository with 796 commits/month
- 9 active contributors with increasing commit trend
- Comprehensive .gitignore configuration

**Gaps:**
- Branch protection rules not confirmed
- No merge request templates

## Build & Integration (Score: 85/100)
**Status:** Detected

**Strengths:**
- pnpm workspaces for monorepo management
- Docker multi-stage builds
- Terraform infrastructure-as-code

**Gaps:**
- 240/370 dependencies outdated (65% drift)
- No automated dependency update tool (Dependabot/Renovate)

## Testing & Quality (Score: 100/100)
**Status:** Detected

**Strengths:**
- Playwright E2E tests for critical flows
- pytest for AI engine component
- Vitest for unit tests

**Note:**
- Coverage threshold configured but not enforced in CI

## Deployment (Score: 100/100)
**Status:** Detected

**Strengths:**
- Kubernetes deployment via Helm charts (dev/staging/prod)
- Terraform manages all GKE infrastructure
- Cloudflare Pages for marketing site
- GitLab CI multi-project pipeline

## Security (Score: 60/100)
**Status:** Partial

**Strengths:**
- HashiCorp Vault for production secrets
- Vault Agent with AppRole and JWT/OIDC auth
- .env files in .gitignore

**Critical Gaps:**
- No vulnerability scanning in CI
- No SAST tool (SonarQube, Semgrep)
- No secret detection (Gitleaks, TruffleHog)

## Collaboration (Score: 20/100)
**Status:** Partial

**Strengths:**
- Well-configured CLAUDE.md for AI assistance

**Gaps:**
- No merge request template
- No issue templates
- No code review guidelines document

## Documentation (Score: 100/100)
**Status:** Detected

**Strengths:**
- 28 documentation files
- Deployment, security, and database guides
- CLAUDE.md with comprehensive project context

## AI Tooling (Score: 40/100)
**Status:** Partial

**Strengths:**
- CLAUDE.md with detailed project context

**Gaps:**
- No AI-powered code review integration
- No AI-assisted PR summaries
`,
  },
  {
    fileName: 'ai-docs/OPEN_QUESTIONS.md',
    title: 'Open Questions',
    content: `## Requires Human Input

### Security Scanning Strategy
- Which SAST tool to adopt? (SonarQube Community, Semgrep, GitLab SAST)
- Should vulnerability scanning block MRs or only warn?
- What is the acceptable risk threshold for dependency CVEs?

### Dependency Management
- Should Renovate automerge patch-level updates?
- Are there dependencies that should be pinned and excluded from automation?
- What is the target drift ratio after cleanup?

### Collaboration Tooling
- What sections should the MR template include?
- Should issue templates differ for bugs vs. features vs. infrastructure?
- Is there a code review SLA target?

### Release Process
- Is semantic versioning planned? (Currently 0 releases in 12 months)
- Should releases be automated on merge to main?
- What changelog format is preferred?

## Assumptions Made

1. **Active Development:** 796 commits/month indicates rapid iteration
2. **Small-to-Medium Team:** 9 contributors, trunk-based development
3. **Pre-Release Stage:** No formal releases yet, focused on feature development
4. **Security Debt Acknowledged:** Vault adoption shows security awareness; scanning gaps are likely known

## Maintenance Burden Assessment

- **Current:** High - manual dependency updates, no automated scanning
- **After Recommendations:** Medium - automated updates, CI-enforced security
- **Estimated Reduction:** ~20% of maintenance burden eliminated
`,
  },
  {
    fileName: 'ai-docs/SUMMARY.md',
    title: 'Executive Summary',
    content: `## Overall Score: 74/100

**Grade: B** - Solid foundation with targeted improvements needed

## Key Findings

This monorepo demonstrates strong engineering practices in deployment, testing, documentation, and build tooling. The primary gap is in security scanning - the team has invested heavily in secrets management (Vault) but has not yet added vulnerability scanning, SAST, or secret detection to the CI pipeline.

## Score Summary

| Category | Score | Weight |
|----------|-------|--------|
| Source Control | 85 | 12.5% |
| Build & Integration | 85 | 12.5% |
| Testing & Quality | 100 | 15% |
| Deployment | 100 | 15% |
| Security | 60 | 15% |
| Collaboration | 20 | 10% |
| Documentation | 100 | 10% |
| AI Tooling | 40 | 10% |

## Strengths

1. **HashiCorp Vault secrets management** - Production-grade secrets with AppRole and JWT/OIDC auth
2. **E2E tests with Playwright** - Critical user flows covered with browser automation
3. **Comprehensive CI pipeline** - GitLab CI with build, test, and deploy stages
4. **Well-configured CLAUDE.md** - Deep AI agent context for development assistance
5. **Full Kubernetes deployment** - Helm charts and Terraform for multi-environment infrastructure

## Critical Gaps

| Gap | Severity | Effort |
|-----|----------|--------|
| No vulnerability scanning | Critical | Medium |
| No SAST tool | High | Medium |
| No secret detection | High | Low |
| No MR template | High | Low |
| No dependency automation | Medium | Low |

## Recommended Next Steps

### Immediate (This Sprint)
1. Add Gitleaks for secret detection (low effort, high impact)
2. Create merge request template (low effort, medium impact)

### Short-term (Next 2 Weeks)
1. Implement SAST with Semgrep or SonarQube
2. Add vulnerability scanning stage to GitLab CI
3. Configure Renovate for automated dependency updates

### Medium-term (Next Month)
1. Enforce coverage thresholds in CI
2. Set up automated CD to staging
3. Establish release process with semantic versioning

## Expected Outcome

Implementing these recommendations would improve the score to **88-92/100** (Grade A), primarily by closing the security scanning gaps and improving collaboration tooling.
`,
  },
];
